/*
 * UNIK collection page (F2-06).
 *
 * Category badges that act as filters (leaf categories in combined mode, rendered by
 * snippets/unik-collection-category-badges.liquid with data-category-filter) go through
 * Colorblock's FacetFiltersForm.renderPage instead of a full page load. The badges live
 * in #UnikCollectionNav, whose content facets.js replaces with the response (F6-03), so
 * focus is moved back to the same badge once the nav has changed. On phones the badges are one
 * swipeable row (F6-03): its scroll position survives the render, and the chosen badge (or
 * the active one on page load) is scrolled into view.
 * Modified clicks (new tab/window) and group badges are ordinary links.
 *
 * <unik-load-more> (F3-01, snippets/unik-load-more.liquid): fetches the next page of the
 * grid section, appends its products to #product-grid and replaces itself with the next
 * page's control (counter and link). A response for a grid that filtering has replaced in
 * the meantime is dropped. The URL is not updated (parity with the Shella reference).
 *
 * Filter drawer (F3-02), progressive enhancement of Colorblock's #FacetFiltersFormMobile:
 *  - facet groups open in place as an accordion instead of Colorblock's sliding submenus
 *    (menu-drawer's summary and Escape handlers are kept from the groups);
 *  - lists with more than five values show five plus "Vis flere", which adds ten per click
 *    (F6-03); checked values always show, and the number shown survives filter responses;
 *  - the price facet gets a two-handle slider above the from/to fields (D-02), which take
 *    whole kroner of at most four digits (F6-03);
 *  - opening the drawer moves focus to its first focusable element (F7-03).
 * facets.js replaces the groups' content with each response, so a MutationObserver limited
 * to the drawer form re-applies the enhancements.
 */
(() => {
  if (window.unikCollection) return;

  let focusObserver = null;
  const BADGE_LIST = '.unik-category-badges__list';

  /**
   * On phones the badges are one swipeable row (F6-03). Centres the badge in the row when it
   * is not fully visible inside the row's padding; only the row scrolls, never the page.
   */
  function revealBadge(badge) {
    const list = badge?.closest(BADGE_LIST);
    if (!list || list.scrollWidth <= list.clientWidth) return;
    const listRect = list.getBoundingClientRect();
    const rect = badge.getBoundingClientRect();
    const style = getComputedStyle(list);
    const start = listRect.left + (parseFloat(style.paddingLeft) || 0);
    const end = listRect.right - (parseFloat(style.paddingRight) || 0);
    if (rect.left >= start && rect.right <= end) return;
    list.scrollLeft += rect.left + rect.width / 2 - (listRect.left + listRect.width / 2);
  }

  function restoreFocusAfterRender(categoryId) {
    const nav = document.getElementById('UnikCollectionNav');
    focusObserver?.disconnect();
    if (!nav || !categoryId) return;
    // facets.js replaces the row, which would start again from the left.
    const scrollLeft = nav.querySelector(BADGE_LIST)?.scrollLeft || 0;
    focusObserver = new MutationObserver(() => {
      focusObserver.disconnect();
      focusObserver = null;
      const list = nav.querySelector(BADGE_LIST);
      if (list) list.scrollLeft = scrollLeft;
      const badge = Array.from(nav.querySelectorAll('[data-category-filter-id]')).find(
        (element) => element.dataset.categoryFilterId === categoryId
      );
      badge?.focus({ preventScroll: true });
      revealBadge(badge);
    });
    focusObserver.observe(nav, { childList: true });
  }

  function onClick(event) {
    const link = event.target.closest?.('a[data-category-filter]');
    if (!link || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (typeof FacetFiltersForm !== 'function' || !document.getElementById('ProductGridContainer')) return;

    event.preventDefault();
    const url = new URL(link.href, window.location.href);
    url.searchParams.delete('page');
    restoreFocusAfterRender(link.dataset.categoryFilterId);
    FacetFiltersForm.renderPage(url.searchParams.toString());
  }

  document.addEventListener('click', onClick);
  revealBadge(document.querySelector(`${BADGE_LIST} .is-active`));

  class UnikLoadMore extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('[data-unik-load-more-link]');
      this.onLinkClick = this.onLinkClick.bind(this);
      this.link?.addEventListener('click', this.onLinkClick);
    }

    disconnectedCallback() {
      this.link?.removeEventListener('click', this.onLinkClick);
    }

    onLinkClick(event) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      // Busy (28.10.2026): a click or Enter while loading does nothing, and never follows the href.
      if (this.link?.getAttribute('aria-disabled') === 'true') return;
      this.load();
    }

    /** The product list: #product-grid itself on collections, its <ul> on the search page. */
    static listIn(root) {
      const grid = root.getElementById('product-grid');
      return grid?.matches('ul') ? grid : grid?.querySelector('ul.product-grid') || null;
    }

    get grid() {
      return UnikLoadMore.listIn(document);
    }

    async load() {
      const grid = this.grid;
      if (this.loading || !this.link || !grid) return;
      this.loading = true;
      this.setBusy(true);
      try {
        const url = new URL(this.link.href, window.location.href);
        url.searchParams.set('section_id', document.getElementById('product-grid').dataset.id);
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        // Filtering replaced the grid while this page was loading: drop the response.
        if (!this.isConnected || grid !== this.grid) return;
        this.append(grid, html);
      } catch (error) {
        if (!this.isConnected) return;
        this.querySelector('[data-unik-load-more-error]')?.removeAttribute('hidden');
        this.setBusy(false);
      } finally {
        this.loading = false;
      }
    }

    append(grid, html) {
      const items = Array.from(UnikLoadMore.listIn(html)?.children || []).filter((item) => item.matches('li'));
      items.forEach((item) => {
        // Colorblock's reveal-on-scroll only initialises on page load; appended cards
        // would stay invisible with these classes.
        item.classList.remove('scroll-trigger', 'animate--slide-in', 'animate--fade-in');
        item.removeAttribute('data-cascade');
        grid.appendChild(document.adoptNode(item));
      });

      // Search pages (F6-03): pages and articles from the next page join the text rows above
      // the filter button.
      const textResults = document.querySelector('[data-unik-search-text-results]');
      const nextTextRows = html.querySelectorAll('[data-unik-search-text-results] li');
      if (textResults && nextTextRows.length) {
        nextTextRows.forEach((row) => textResults.querySelector('ul').appendChild(document.adoptNode(row)));
        textResults.hidden = false;
      }

      const next = html.querySelector('unik-load-more');
      const replacement = next ? document.adoptNode(next) : null;
      if (replacement) this.replaceWith(replacement);
      else this.remove();

      const status = replacement?.querySelector('[data-unik-load-more-status]');
      if (status) status.textContent = (this.dataset.messageLoaded || '').replace('__count__', String(items.length));
      // Focus the first new product: the card's title link, not its brand link (search
      // pages can also list pages and articles, so fall back to the first visible link).
      const links = Array.from(items[0]?.querySelectorAll('a[href]') || []);
      (links.find((link) => link.matches('.unik-product-card__link')) || links.find((link) => link.getClientRects().length) || links[0])?.focus({ preventScroll: true });
    }

    /**
     * Busy (28.10.2026): the link shows the shared .unik-button spinner (aria-busy, unik-base.css)
     * and is inert (aria-disabled: a link cannot be disabled). Screen readers do not announce
     * aria-busy on a link, so the status region gets the loading message; it is cleared again
     * on failure, and the next page's control replaces it on success.
     */
    setBusy(busy) {
      const status = this.querySelector('[data-unik-load-more-status]');
      if (busy) {
        this.setAttribute('aria-busy', 'true');
        this.link?.setAttribute('aria-busy', 'true');
        this.link?.setAttribute('aria-disabled', 'true');
        this.querySelector('[data-unik-load-more-error]')?.setAttribute('hidden', '');
        if (status) status.textContent = this.dataset.messageLoading || '';
      } else {
        this.removeAttribute('aria-busy');
        this.link?.removeAttribute('aria-busy');
        this.link?.removeAttribute('aria-disabled');
        if (status) status.textContent = '';
      }
    }
  }

  if (!customElements.get('unik-load-more')) customElements.define('unik-load-more', UnikLoadMore);

  // --- Filter drawer (F3-02) -----------------------------------------------------------

  const SHOW_LIMIT = 5;
  const SHOW_STEP = 10;
  const shownInLists = new Map();

  function strings() {
    return document.getElementById('UnikCollectionStrings')?.dataset || {};
  }

  /** Reads "2.199,00", "2 199,00" or "2199" (the store's money format uses "." for thousands). */
  function parseAmount(value) {
    let text = String(value || '').replace(/[\s\u00a0]/g, '');
    if (text.includes(',')) text = text.replace(/\./g, '').replace(',', '.');
    else if (/^\d{1,3}(\.\d{3})+$/.test(text)) text = text.replace(/\./g, '');
    const amount = parseFloat(text);
    return Number.isFinite(amount) ? amount : null;
  }

  function moreIcon(expandable) {
    // Lucide plus / minus, 1.5 px stroke like the other UNIK icons.
    const paths = expandable ? '<path d="M5 12h14"></path><path d="M12 5v14"></path>' : '<path d="M5 12h14"></path>';
    return `<svg class="unik-facets-more__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${paths}</svg>`;
  }

  /**
   * F6-03 Five values, then "Vis flere +" shows ten more per click until the list is complete,
   * when the button becomes "Vis mindre −" and goes back to five. Checked values always show.
   * The number shown survives facets.js replacing the group content.
   */
  function enhanceLists(form) {
    const text = strings();
    form.querySelectorAll('.mobile-facets__details').forEach((details) => {
      const list = details.querySelector('.mobile-facets__list');
      if (!list || list.dataset.unikShowMore) return;
      list.dataset.unikShowMore = 'true';
      details.querySelectorAll('.unik-facets-more').forEach((button) => button.remove());

      const extra = Array.from(list.children)
        .slice(SHOW_LIMIT)
        .filter((item) => !item.querySelector('input:checked'));
      if (!extra.length) return;

      const key = details.id;
      list.id ||= `${key}-list`;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'unik-facets-more';
      button.setAttribute('aria-controls', list.id);
      const update = (shown) => {
        extra.forEach((item, position) => {
          item.hidden = position >= shown;
        });
        const complete = shown >= extra.length;
        button.setAttribute('aria-expanded', String(shown > 0));
        button.innerHTML = moreIcon(!complete);
        const label = document.createElement('span');
        label.textContent = (complete ? text.showLess : text.showMore) || '';
        button.prepend(label);
      };
      button.addEventListener('click', () => {
        const current = Math.min(shownInLists.get(key) || 0, extra.length);
        const shown = current >= extra.length ? 0 : Math.min(current + SHOW_STEP, extra.length);
        if (shown) shownInLists.set(key, shown);
        else shownInLists.delete(key);
        update(shown);
      });
      list.after(button);
      update(Math.min(shownInLists.get(key) || 0, extra.length));
    });
  }

  const PRICE_DIGITS = 4;
  // The slider and the max field go to 5000 kr in every collection; the placeholder shows the
  // collection's highest price.
  const PRICE_MAX = 5000;

  /**
   * F6-03 design: the slider above "Min"/"Maks" fields in whole kroner, at most four digits,
   * with the currency after the number (styled in unik-collection.css). The fields keep
   * Colorblock's names, so facets.js and price-range submit and clamp them as before.
   */
  function enhancePrice(form) {
    const text = strings();
    form.querySelectorAll('price-range').forEach((range, index) => {
      if (range.querySelector('.unik-price-slider')) return;
      const [minInput, maxInput] = range.querySelectorAll('input.field__input');
      // The max field's data-max is the range maximum; Colorblock overwrites the min field's.
      const highest = Math.ceil(parseAmount(maxInput?.dataset.max) || 0);
      if (!minInput || !maxInput || highest <= 0) return;
      const max = Math.max(PRICE_MAX, highest);

      const whole = (input, round) => {
        const amount = parseAmount(input.value);
        input.value = amount === null ? '' : String(round(amount)).slice(0, PRICE_DIGITS);
      };
      whole(minInput, Math.floor);
      whole(maxInput, Math.ceil);
      maxInput.placeholder = String(highest);
      // Whole numbers for Colorblock's clamping on change (it reads these with Number()).
      minInput.dataset.min = '0';
      minInput.dataset.max = maxInput.value || String(max);
      maxInput.dataset.min = minInput.value || '0';
      maxInput.dataset.max = String(max);
      [minInput, maxInput].forEach((input) => {
        input.maxLength = PRICE_DIGITS;
        input.inputMode = 'numeric';
        input.autocomplete = 'off';
      });
      range.classList.add('unik-price');
      minInput.closest('.field')?.classList.add('unik-price__field', 'unik-price__field--min');
      maxInput.closest('.field')?.classList.add('unik-price__field', 'unik-price__field--max');
      range.querySelectorAll('.field-currency').forEach((currency, position) => {
        currency.classList.add('unik-price__currency', `unik-price__currency--${position ? 'max' : 'min'}`);
      });

      const slider = document.createElement('div');
      slider.className = 'unik-price-slider';
      const handle = (bound, label) => {
        const input = document.createElement('input');
        input.type = 'range';
        input.min = '0';
        input.max = String(max);
        input.step = '1';
        input.id = `UnikPrice-${bound}-${index}`;
        input.className = `unik-price-slider__input unik-price-slider__input--${bound}`;
        input.setAttribute('aria-label', label || '');
        slider.appendChild(input);
        return input;
      };
      const low = handle('min', text.priceMin);
      const high = handle('max', text.priceMax);

      const fromFields = () => {
        low.value = String(Math.max(0, Math.min(parseAmount(minInput.value) ?? 0, max)));
        high.value = String(Math.max(0, Math.min(parseAmount(maxInput.value) ?? max, max)));
        slider.style.setProperty('--unik-price-low', `${(Number(low.value) / max) * 100}%`);
        slider.style.setProperty('--unik-price-high', `${(Number(high.value) / max) * 100}%`);
      };
      const toFields = (event) => {
        if (Number(low.value) > Number(high.value)) {
          if (event.target === low) low.value = high.value;
          else high.value = low.value;
        }
        // The extremes mean "no limit", so no price pill appears for the full range.
        minInput.value = Number(low.value) > 0 ? low.value : '';
        maxInput.value = Number(high.value) < max ? high.value : '';
        fromFields();
      };
      // Digits only; runs before facets.js reads the form on the same input event.
      const typed = (event) => {
        const digits = event.target.value.replace(/\D/g, '').slice(0, PRICE_DIGITS);
        if (digits !== event.target.value) event.target.value = digits;
        fromFields();
      };
      low.addEventListener('input', toFields);
      high.addEventListener('input', toFields);
      minInput.addEventListener('input', typed);
      maxInput.addEventListener('input', typed);
      // First in the group, so the tab order follows the layout (slider above the fields).
      range.prepend(slider);
      fromFields();
    });
  }

  const FOCUSABLE = 'summary, a[href], button:enabled, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, [tabindex]:not([tabindex^="-"])';

  /** First element in `container` that can take focus now (not inside a closed group). */
  function firstFocusable(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE)).find((element) => {
      if (element.closest('[hidden]')) return false;
      const closed = element.closest('details:not([open])');
      if (closed && element.parentElement !== closed) return false;
      return element.checkVisibility?.() ?? true;
    });
  }

  function enhanceDrawer() {
    const form = document.getElementById('FacetFiltersFormMobile');
    if (!form) return;
    if (!form.dataset.unikDrawer) {
      form.dataset.unikDrawer = 'true';
      // Accordion: the group's <details> toggles natively; menu-drawer would slide it in
      // as a submenu and trap focus there.
      form.addEventListener(
        'click',
        (event) => {
          const summary = event.target.closest?.('.mobile-facets__details > summary');
          if (!summary) return;
          event.stopPropagation();
          summary.setAttribute('aria-expanded', String(!summary.parentElement.open));
        },
        true
      );
      // Escape closes the whole drawer, not only the open group.
      form.addEventListener(
        'keyup',
        (event) => {
          if (event.code?.toUpperCase() !== 'ESCAPE' || !event.target.closest?.('.mobile-facets__details')) return;
          const drawer = form.closest('menu-drawer');
          const main = drawer?.querySelector('.mobile-facets__disclosure > summary');
          if (!drawer || !main) return;
          event.stopPropagation();
          drawer.closeMenuDrawer(event, main);
          main.focus();
        },
        true
      );
      // F7-03 Opening the drawer moves focus into it. Colorblock's menu-drawer (as Dawn)
      // leaves focus on the "Filtrer" toggle, so the next Tab reached the close icon in
      // the toggle or, in WebKit, left the drawer. 'toggle' fires after menu-drawer's click
      // handler has focused the toggle. Rings still show only for the keyboard (D-20).
      const disclosure = form.closest('details');
      disclosure?.addEventListener('toggle', () => {
        const inner = form.querySelector('.mobile-facets__inner');
        if (!disclosure.open || !inner || inner.contains(document.activeElement)) return;
        const target = firstFocusable(inner);
        if (!target) inner.setAttribute('tabindex', '-1');
        (target || inner).focus({ preventScroll: true });
      });
      new MutationObserver(() => {
        enhanceLists(form);
        enhancePrice(form);
      }).observe(form, { childList: true, subtree: true });
    }
    enhanceLists(form);
    enhancePrice(form);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', enhanceDrawer);
  else enhanceDrawer();

  window.unikCollection = { onClick, enhanceDrawer };
})();
